import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'
import { match, P } from 'ts-pattern'

import { useTranslation } from '@root/shared/localization'
import { mediaMaxDesktopSmall } from '@root/shared/ui-kit'
import { PageHeader, PageSection } from '@root/shared/ui-kit/desktop'
import { layoutHeightCss } from '@root/shared/ui-kit/utils/page-layout'

import { walletsManagerModelAtom } from '../atoms'

import { EditWallet } from './side/edit-wallet'
import { NewWallet } from './side/new-wallet'
import { WalletManagerTransactions } from './side/transactions'
import { WalletDetailsDeposit } from './side/wallet-details-deposit'
import { WalletDetailsExport } from './side/wallet-details-export'
import { WalletDetailsImport } from './side/wallet-details-import'
import { WalletsDetailsSwap } from './side/wallet-details-swap'
import { WalletDetailsTransfer } from './side/wallet-details-transfer'
import { WalletStatsShort } from './wallet-stats-short/wallet-stats-short'
import { WalletsHeader } from './wallets-header/wallets-header'
import { PortfolioWalletsTable } from './wallets-table/portfolio-wallets-table'
import { WalletsTableFooter } from './wallets-table/wallets-table-footer'

type Props = {}

export const WalletManagerPage: React.FC<Props> = () => {
  const { t } = useTranslation()
  const [model] = useAtom(walletsManagerModelAtom)
  const [screen, setScreen] = useAtom(model.walletDetailsScreen)
  const [selectedWallet] = useAtom(model.selectedWallet)

  const renderSideContent = () => {
    return match({ wallet: selectedWallet, screen })
      .with({ screen: 'deposit', wallet: P.nonNullable }, ({ wallet }) => (
        <WalletDetailsDeposit key={wallet.id} wallet={wallet} />
      ))
      .with({ screen: 'swap', wallet: P.nonNullable }, ({ wallet }) => (
        <WalletsDetailsSwap key={wallet.id} wallet={wallet} />
      ))
      .with({ screen: 'transfer', wallet: P.nonNullable }, ({ wallet }) => (
        <WalletDetailsTransfer key={wallet.id} wallet={wallet} />
      ))
      .with({ screen: 'edit', wallet: P.nonNullable }, ({ wallet }) => (
        <EditWallet key={wallet.id} wallet={wallet} />
      ))
      .with({ screen: 'export', wallet: P.nonNullable }, ({ wallet }) => (
        <WalletDetailsExport key={wallet.id} wallet={wallet} />
      ))
      .with({ screen: 'new-wallet' }, () => <NewWallet />)
      .with({ screen: 'import-wallet' }, () => (
        <WalletDetailsImport onClose={() => setScreen('transactions')} />
      ))
      .otherwise(() => <WalletManagerTransactions />)
  }

  return (
    <Root>
      <PageHeader>
        <PageHeader.Title>{t('wallet-manager.page.title')}</PageHeader.Title>
        <WalletStatsShort />
      </PageHeader>
      <Content>
        <TableSection>
          <WalletsHeader />
          <PortfolioWalletsTable />
          <WalletsTableFooter />
        </TableSection>
        <Side>{renderSideContent()}</Side>
      </Content>
    </Root>
  )
}

const Root = styled.div`
  ${layoutHeightCss}

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const Content = styled.div`
  min-height: 0;
  overflow: hidden;

  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  padding: 10px;
  gap: 10px;

  ${mediaMaxDesktopSmall(css`
    grid-template-columns: minmax(0, 1fr) 300px;
  `)}
`
const Side = styled(PageSection)`
  min-height: 0;
  height: 100%;
  display: grid;
`

const TableSection = styled(PageSection)`
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
`
